import { InstanceTarget } from '../types/instance/InstanceTarget';
import { InstanceFormData, InstanceFormValidation } from '../types/instance/InstanceFormState';
import ThirdPartyLinksType from '../types/ThirsPartyLinks';
import Tags from '../types/Tags';
import Helper from './Helper';

export class InstanceFormValidator {
    
    static validateName(
        name: string, 
        currentId: number, 
        instances: InstanceTarget[]
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedName = name.trim();
        
        if (trimmedName === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        // Check for duplicate names
        const duplicateIndex = instances.findIndex(
            instance => instance.id !== currentId && 
            instance.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (duplicateIndex !== -1) {
            return {
                isValid: false,
                isUsed: true,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validateHost(
        host: string
    ): { isValid: boolean; isUsed: boolean; className: string } {
        const trimmedHost = host.trim();
        
        if (trimmedHost === '') {
            return {
                isValid: false,
                isUsed: false,
                className: 'is-invalid'
            };
        }

        // Note: Original code sets hostUsed to false even for duplicates
        // Keeping the same logic for consistency
        return {
            isValid: true,
            isUsed: false,
            className: 'is-valid'
        };
    }

    static validatePort(port: number): { isValid: boolean; className: string } {
        const portString = port.toString();
        const regexp = /^[0-9\b]+$/;

        if ((portString.length > 0 || regexp.test(portString)) && 
            (portString.length < 6) && 
            (port <= 65535)) {
            return {
                isValid: true,
                className: 'is-valid'
            };
        }

        return {
            isValid: false,
            className: 'is-invalid'
        };
    }

    static validateType(type: string): { isValid: boolean; className: string } {
        if (type.trim() === '') {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateAgentId(agentId: number): { isValid: boolean; className: string } {
        if (agentId === 0) {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateUrl(url: string, isAdvanced: boolean): { isValid: boolean; className: string } {
        if (isAdvanced && url.trim() === '') {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateDatabase(database: string, type: string): { isValid: boolean; className: string } {
        // Database is required for CockroachDB
        if (type === 'cockroachdb' && database.trim() === '') {
            return {
                isValid: false,
                className: 'is-invalid'
            };
        }

        return {
            isValid: true,
            className: 'is-valid'
        };
    }

    static validateThirdPartyLinks(links: ThirdPartyLinksType[]): {
        isValid: boolean;
        updatedLinks: ThirdPartyLinksType[];
    } {
        let isValid = true;
        const updatedLinks = links.map(link => ({
            ...link,
            validName: link.name.trim() !== '',
            validUrl: link.url.trim() !== ''
        }));

        updatedLinks.forEach(link => {
            if (!link.validName || !link.validUrl) {
                isValid = false;
            }
        });

        return { isValid, updatedLinks };
    }

    static validateTags(tags: Tags[]): {
        isValid: boolean;
        updatedTags: Tags[];
        hasDuplicates: boolean;
    } {
        let isValid = true;
        const updatedTags = tags.map(tag => ({
            ...tag,
            validName: tag.tagname.trim() !== '',
            validValue: tag.tagvalue.trim() !== ''
        }));

        updatedTags.forEach(tag => {
            if (!tag.validName || !tag.validValue) {
                isValid = false;
            }
        });

        // Check for duplicate tag names
        const hasDuplicates = Helper.checkForTwoWithSameKeyValue('tagname', tags);
        if (hasDuplicates) {
            isValid = false;
        }

        return { isValid, updatedTags, hasDuplicates };
    }

    static validateCompleteForm(
        formData: InstanceFormData,
        instances: InstanceTarget[]
    ): {
        isValid: boolean;
        validation: InstanceFormValidation;
        updatedLinks: ThirdPartyLinksType[];
        updatedTags: Tags[];
    } {
        let isValid = true;

        // Validate name
        const nameValidation = this.validateName(formData.name, formData.id, instances);
        if (!nameValidation.isValid) isValid = false;

        // Validate host
        const hostValidation = this.validateHost(formData.host);
        if (!hostValidation.isValid) isValid = false;

        // Validate type
        const typeValidation = this.validateType(formData.type);
        if (!typeValidation.isValid) isValid = false;

        // Validate agent
        const agentValidation = this.validateAgentId(formData.agentId);
        if (!agentValidation.isValid) isValid = false;

        // Validate URL if in advanced mode
        const urlValidation = this.validateUrl(formData.url, formData.advanced);
        if (!urlValidation.isValid) isValid = false;

        // Validate port
        const portValidation = this.validatePort(formData.port);
        if (!portValidation.isValid) isValid = false;

        // Validate database (required for CockroachDB)
        const databaseValidation = this.validateDatabase(formData.database, formData.type);
        if (!databaseValidation.isValid) isValid = false;

        // Validate links
        const linksValidation = this.validateThirdPartyLinks(formData.links);
        if (!linksValidation.isValid) isValid = false;

        // Validate tags
        const tagsValidation = this.validateTags(formData.tags);
        if (!tagsValidation.isValid) isValid = false;

        const validation: InstanceFormValidation = {
            name: nameValidation,
            host: hostValidation,
            type: typeValidation,
            agentId: agentValidation,
            port: portValidation,
            url: urlValidation,
            database: databaseValidation,
            username: { className: '' },
            password: { className: '', elementType: 'password' },
            isFormValid: isValid
        };

        return {
            isValid,
            validation,
            updatedLinks: linksValidation.updatedLinks,
            updatedTags: tagsValidation.updatedTags
        };
    }
}

// Utility functions for business logic
export class InstanceFormUtils {
    
    static requireDatabase(type: string): boolean {
        return ['cockroachdb', 'db2', 'informix', 'oracle', 'postgresql'].includes(type);
    }

    static requireServer(type: string): boolean {
        return type === 'informix';
    }

    static hasStatisticsCollection(type: string): boolean {
        // This would need to be imported from constants, but avoiding circular deps for now
        const STATISTICS_DISABLED_INSTANCE_TYPES = ['generic']; // placeholder
        return !STATISTICS_DISABLED_INSTANCE_TYPES.includes(type);
    }

    static canViewPassword(isCreate: boolean, isUpdate: boolean, passwordChanged: boolean): boolean {
        return isCreate || (isUpdate && passwordChanged);
    }

    static getDefaultCollectBlockingSessions(type: string, defaultValues: any[]): boolean {
        const defaults = defaultValues.find(item => item.type === type);
        return defaults ? defaults.defaultcollectblockingsessions : false;
    }

    static buildDynamicUrl(
        urlFormat: string,
        host: string,
        port: number,
        server: string,
        database: string
    ): string {
        return urlFormat
            .replace('<host>', host)
            .replace('<port>', port.toString())
            .replace('<server>', server)
            .replace('<database>', database);
    }

    static sanitizeNumericInput(value: string): string {
        return value.replace(/\D/g, ""); // Remove non-digits
    }

    static hasIntegratedSecurity(parameters: string | undefined): boolean {
        return (parameters || '')
            .split(';')
            .some(param => param.trim().toLowerCase() === 'integratedsecurity=true');
    }

    static setIntegratedSecurity(parameters: string | undefined, enabled: boolean): string | undefined {
        const params = (parameters || '')
            .split(';')
            .map(param => param.trim())
            .filter(param => param && param.split('=')[0].trim().toLowerCase() !== 'integratedsecurity');
        if (enabled) {
            params.push('integratedSecurity=true');
        }
        return params.length > 0 ? params.join(';') : undefined;
    }
}
